import React, { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import { PageHeading } from '@/components/ui/page-heading';
import { Card, CardContent } from '@/components/ui/card';
import { Store, AlertCircle } from 'lucide-react';
import { useAuth } from '@/context/auth';
import { hasOrgAdminAccess } from '@/context/auth/types';
import { supabase } from '@/integrations/supabase/client';
import RestaurantTable from '@/components/admin/restaurants/RestaurantTable';
import RestaurantEditDialog from '@/components/admin/restaurants/RestaurantEditDialog';

interface Restaurant {
  restaurant_id: string;
  restaurant_name: string;
  address: string | null;
  status: string | null;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  ubereats_commission_rate: number | null;
  ubereats_markup: number | null;
  google_review_url: string | null;
  facebook_url: string | null;
  instagram_url: string | null;
  ordering_page_url: string | null;
  review_page_url: string | null;
  organisation_id: string | null;
}

const RestaurantsPage: React.FC = () => {
  const { user } = useAuth();
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const userRole = user?.jwt_role || user?.role;
  const hasPermission = hasOrgAdminAccess(userRole || '');
  const organisationId = user?.organisationId;

  const fetchOrgRestaurants = async () => {
    if (!organisationId) {
      setError('No organisation found for your account');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('restaurants_master')
        .select(`
          restaurant_id,
          restaurant_name,
          address,
          status,
          logo_url,
          primary_color,
          secondary_color,
          ubereats_commission_rate,
          ubereats_markup,
          google_review_url,
          facebook_url,
          instagram_url,
          ordering_page_url,
          review_page_url,
          organisation_id
        `)
        .eq('organisation_id', organisationId)
        .order('restaurant_name');

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      setRestaurants(data || []);
    } catch (err: any) {
      console.error('Error fetching restaurants:', err);
      setError(err.message || 'Failed to load restaurants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasPermission) {
      fetchOrgRestaurants();
    } else {
      setLoading(false);
    }
  }, [organisationId, hasPermission]);

  const handleEditRestaurant = (restaurant: Restaurant) => {
    setSelectedRestaurant(restaurant);
    setEditDialogOpen(true);
  };

  const handleRestaurantSaved = () => {
    fetchOrgRestaurants();
  };

  if (!hasPermission) {
    return (
      <Navigation>
        <div className="space-y-6">
          <PageHeading
            title="Restaurants"
            description="Manage your organisation's restaurants"
          />

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <div>
                  <h3 className="font-medium">Access Denied</h3>
                  <p className="text-sm text-muted-foreground">
                    You do not have permission to access this page. Organisation admin access is required.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </Navigation>
    );
  }

  return (
    <Navigation>
      <div className="space-y-6">
        <PageHeading
          title="Restaurants"
          description="Manage your organisation's restaurants"
        />

        <Card>
          <CardContent className="p-6">
            {error ? (
              <div className="flex items-center gap-3 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <div>
                  <h3 className="font-medium">Error Loading Restaurants</h3>
                  <p className="text-sm text-muted-foreground">{error}</p>
                </div>
              </div>
            ) : restaurants.length === 0 && !loading ? (
              <div className="text-center py-8">
                <Store className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Restaurants Found</h3>
                <p className="text-muted-foreground">
                  No restaurants are associated with your organisation yet.
                </p>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Store className="h-5 w-5 text-muted-foreground" />
                  <span className="font-medium">
                    {restaurants.length} {restaurants.length === 1 ? 'Restaurant' : 'Restaurants'}
                  </span>
                </div>
                <RestaurantTable
                  restaurants={restaurants}
                  loading={loading}
                  onEditRestaurant={handleEditRestaurant}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Restaurant Dialog */}
        <RestaurantEditDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          restaurant={selectedRestaurant}
          organisationId={organisationId || ''}
          onSave={handleRestaurantSaved}
        />
      </div>
    </Navigation>
  );
};

export default RestaurantsPage;
